/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit, signal} from '@angular/core';
import {HttpEventType, HttpResponse} from '@angular/common/http';
import JSZip from 'jszip';
import {ToastMessageOptions} from 'primeng/api';
import {EsciComponent} from '../esci.component';
import {CommonHelperService} from '../../service/common-helper.service';

@Component({
    selector: 'app-crm2',
    templateUrl: './crm2.component.html',
    styleUrls: ['./crm2.component.scss'],
    standalone: false
})

export class Crm2Component extends EsciComponent implements OnInit {
  harnessMatrixFiles = new Array<File>();
  harnessMatrixFileBlob: Blob | null;
  progress = signal(0);
  permissionUrl = '/esciUI/crm2Permission';
  crm2Messages: ToastMessageOptions[] = [];

  private setProgress(value: number): void {
    this.progress.set(value);
  }

  ngOnInit(): void {
    this.permission.set(false);
    this.esciService.getPermissionAndFileSizeLimit(this);
  }

  generateCrm2DisabledReason(): string {
    if (!this.harnessMatrixFiles?.length) {
      return 'Select 2 or more Harness Matrix files to generate Consolidated RPO Matrix';
    }
    if (this.harnessMatrixFiles.length < 2) {
      return 'Select at least one more Harness Matrix file to generate Consolidated RPO Matrix';
    }
    return '';
  }

  generateCrm2(): void {
    if (this.harnessMatrixFiles?.length >= 2 && this.permission()) {
      this.crm2Messages = [];
      this.setProgress(10);
      const zip = new JSZip();
      for (const file of this.harnessMatrixFiles) {
        zip.file(file.name, file);
      }
      zip.generateAsync({type: 'blob'}).then((content) => {
        this.harnessMatrixFileBlob = content;
        this.uploadService.uploadFilesForCrm2(this.harnessMatrixFileBlob).subscribe({
          next: (event: any) => {
            if (event.type === HttpEventType.UploadProgress) {
              this.setProgress(Math.max(10, Math.round(100 * event.loaded / event.total)));
            } else if (event instanceof HttpResponse) {
              this.setProgress(0);
              const result = event.body;
              if (result) {
                this.handleResultCodes(result);
              } else {
                this.crm2Messages.push({severity: 'error', summary: 'Error', detail: 'Could not generate Consolidated RPO Matrix. Please check the files provided and try again later.'});
              }
            }
          },
          error: (err: any) => {
            this.setProgress(0);
            console.log(err);
            this.crm2Messages.push({severity: 'error', summary: 'Error', detail: 'Could not generate Consolidated RPO Matrix. Please check the files provided and try again later.'});
          }
        });
      });
    }
  }

  private handleResultCodes(holder: any): void {
    const codes: string[] = holder?.resultCodeSet;
    if (!codes?.length) {
      this.crm2Messages.push({severity: 'error', summary: 'Error', detail: 'Could not generate Consolidated RPO Matrix. Please check the files provided and try again later.'});
      return;
    }

    const invalidFiles: string[] = holder?.invalidFileSet ?? [];
    const duplicateHarnesses: string[] = holder?.duplicateHarnessSet ?? [];

    for (const code of codes) {
      if (code === 'SUCCESS' && holder.outputFile) {
        const blobFile = new Blob([this.s2ab(window.atob(holder.outputFile))], {type: ''});
        const fileName = 'ConsolidatedRPOMatrix_' + CommonHelperService.getDateString() + '.xlsx';
        CommonHelperService.downloadFile(new Blob([blobFile]), fileName);
        this.crm2Messages.push({severity: 'success', summary: 'Success', detail: 'Consolidated RPO Matrix successfully generated. Please see the downloaded file.'});
      } else if (code === 'INVALID_WORKBOOK') {
        this.crm2Messages.push({severity: 'error', summary: 'Error', detail: 'No valid harness matrix files found to process.'});
      } else if (code === 'INVALID_BAD_FILES') {
        const detail = invalidFiles.length ? ': ' + invalidFiles.join(', ') : '';
        this.crm2Messages.push({severity: 'error', summary: 'Error', detail: 'Some files were invalid and skipped' + detail + '.'});
      } else if (code === 'INVALID_DUPLICATES') {
        const detail = duplicateHarnesses.length ? ': ' + duplicateHarnesses.join(', ') : '';
        this.crm2Messages.push({severity: 'error', summary: 'Error', detail: 'Duplicate harness names found across input files' + detail + '.'});
      } else if (code === 'CLEANUP_FILES_FAILED') {
        this.crm2Messages.push({severity: 'error', summary: 'Error', detail: 'Failed to clean up temporary files after processing.'});
      } else if (code === 'ERROR_MESSAGE') {
        this.crm2Messages.push({severity: 'error', summary: 'Error', detail: 'An error occurred during processing. Please check logs for details.'});
      }
    }
  }

  // Converts a binary string to an ArrayBuffer for Blob creation
  s2ab(s: string): ArrayBuffer {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i !== s.length; ++i) {
      view[i] = s.charCodeAt(i) & 0xFF;
    }
    return buf;
  }
}
